import React, { useState } from 'react';
import Field from '../Field';
import { clashingTag, duplicateTagMessage } from '../../domain/tags';
import s from './add/addStudent.module.css';

// A picker over the studio's managed tags, with create-inline (#138).
//
// Here rather than under `add/` because `EditStudentDetailsDrawer` uses it
// too (#328), where an existing student can leave a tag: `saveTag`'s
// `membershipDelta` computes both halves (#198) and the caller writes them.
// The stylesheet stays in `add/`, whose `.input` and `.checkRow` are shared
// with the wizard's other fields.
//
// MMS's Add New Student → step 1 → `Additional Details`:
//
//   Instrument Tags (Optional)   [picker]  Create Instrument Tag   Go to Tags
//   Group Tags      (Optional)   [picker]  Create Group Tag        Go to Tags
//
// the three-part control `Category` has (#110). This is a tick list over the
// studio's tags of one kind, plus a box that creates one without leaving the
// form. No `Go to Tags`: this lives inside a four-step wizard holding a
// half-typed student, and a link to another screen would discard it. What MMS
// does there is unchecked.
//
// Each tag is its own value, ticked and unticked one at a time; a `<datalist>`
// over a comma-separated box filters and selects against the whole value
// (`StudentDetailsStep`'s header).
//
// Creating one mints its id here: `saveTag` in data/useTags.jsx returns
// nothing, so a created tag would have no id to select by. The wizard hands
// the id to `saveTag` on Save, so a tag created here becomes a record when the
// student does, and abandoning the wizard leaves no orphan tag.
//
// ── An empty list is two different facts ─────────────────────────────────
//
// *"No instrument tags yet."* — MMS's sentence — is true of a studio with no
// tags, not of one whose tags have not been read, and a teacher told so would
// create a tag that already exists. `loaded` tells the two apart: three states
// in the render — unread, read-and-empty, read-and-full. It defaults to `true`
// for a caller with a fixed list (a test); the two live callers pass `useTags`'
// own `loaded`.
export default function TagPickerField({
  label, kind, kindLabel, tags = [], allTags = [], selected, onToggle, onCreate,
  loaded = true,
}) {
  const [draft, setDraft] = useState('');

  const trimmed = draft.trim();
  // The same refusal the Tags tab's own drawer gives, from the same function,
  // so a name refused there is refused here in the same words. `selfId` is null
  // because nothing being created can clash with itself.
  const clash = trimmed ? clashingTag(trimmed, kind, null, allTags) : null;
  const createError = clash ? duplicateTagMessage(clash.label) : null;

  const create = () => {
    if (!trimmed || createError) return;
    onCreate(trimmed);
    setDraft('');
  };

  return (
    <Field label={label} hint="Optional — pick from the studio’s tags, or add one below">
      {tags.length ? (
        <div className={s.tagList}>
          {tags.map((t) => (
            <label key={t.id} className={s.checkRow}>
              <input
                type="checkbox"
                checked={selected.has(t.id)}
                onChange={() => onToggle(t.id)}
              />
              <span>{t.label}</span>
            </label>
          ))}
        </div>
      ) : (
        // MMS's own empty state, said rather than drawn as an empty box, and
        // only once the window has answered (see the header).
        <div className={s.tagEmpty}>
          {loaded ? `No ${kindLabel.toLowerCase()}s yet.` : 'Loading the studio’s tags…'}
        </div>
      )}

      <div className={s.tagCreate}>
        <input
          className={s.input}
          aria-label={`Create ${kindLabel}`}
          placeholder={`Create ${kindLabel}`}
          value={draft}
          maxLength={99}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter creates the tag rather than submitting the wizard, which is
            // what a bare Enter in a drawer would otherwise do.
            if (e.key !== 'Enter') return;
            e.preventDefault();
            create();
          }}
        />
        <button
          type="button" className={s.tagAdd}
          disabled={!trimmed || Boolean(createError)}
          onClick={create}
        >
          Add
        </button>
      </div>
      {createError ? <div className={s.tagError}>{createError}</div> : null}
    </Field>
  );
}
